import { observable, computed, action } from 'mobx';
import { ILoanQuoteDto, ILoanDto, LoanRangesDto } from '../models/dtos/loan';
import { IPagedResultDto } from '../models/dtos/pagedResult';

export class LoanStore {
  @observable
  private loanQuote: ILoanQuoteDto = {
    loanAmount: 3000,
    interest: 0,
    convenienceFee: 0,
    total: 0,
    referenceId: '',
    paymentDetails: [],
    loanDocuments: [],
  };

  @observable
  private _maximumLoanAmount: number = 0;

  @observable
  loanRequested: ILoanDto | undefined;

  @action
  setLoanQuote(loanQuote: ILoanQuoteDto) {
    this.loanQuote = loanQuote;
  }

  @observable
  termNumber: number = 0;

  @action
  setTermNumber(term: number) {
    this.termNumber = term;
  }

  @action
  resetLoanQuote() {
    this.setLoanQuote({
      loanAmount: 3000,
      interest: 0,
      convenienceFee: 0,
      total: 0,
      referenceId: '',
      paymentDetails: [],
      loanDocuments: [],
    });
    this.resetMaximumLoanAmount();
  }

  @computed
  get hasLoanQuote() {
    return this.loanQuote !== undefined && this.loanQuote.referenceId !== '';
  }

  @computed
  get currentLoanQuote() {
    return this.loanQuote;
  }

  @computed
  get amountToReceive(): number {
    // Falls back to the requested amount when the BE has not shipped
    // amountReceive yet (fee-model rollout, UAC-04 of epic b.iii.4)
    return this.loanQuote.amountReceive != null
      ? this.loanQuote.amountReceive
      : this.loanQuote.loanAmount;
  }

  @computed
  get paymentPerPeriod(): number {
    return this.loanQuote.paymentDetails.length > 0
      ? this.loanQuote.paymentDetails[0].paymentPerPeriod
      : 0;
  }

  @computed
  get dueDates(): string[] {
    return this.loanQuote.paymentDetails.map(pd => pd.dueDate);
  }

  @action
  setLoanRequested(loan: ILoanDto) {
    this.loanRequested = loan;
  }

  @action
  setMaximumLoanAmount(amount: number) {
    this._maximumLoanAmount = amount;
  }

  @action
  resetMaximumLoanAmount() {
    this.setMaximumLoanAmount(0);
  }

  @action
  setLoanAmount(amount: number) {
    this.loanQuote.loanAmount = amount;
  }

  @computed
  get maximumLoanAmount(): number {
    return this._maximumLoanAmount;
  }

  /**
   * Loan History
   */
  @observable
  // private _history: ILoanDto[] = [];
  private _history: IPagedResultDto<ILoanDto[]>;

  defaultLoanHistory() {
    return {
      data: [],
      pagination: {
        currentPage: 0,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 0,
        links: {
          last: '',
          next: '',
          previous: '',
        },
      },
    };
  }

  @action
  // setLoanHistory(history: ILoanDto[]) {
  //   this._history = history;
  // }
  setLoanHistory(history: IPagedResultDto<ILoanDto[]>) {
    this._history = history;
  }

  @computed
  get loanHistory() {
    return this._history ? this._history : this.defaultLoanHistory();
  }

  @observable
  private _loanRanges: LoanRangesDto;

  @observable
  private _loanRangesError: { status: number; message: string } | null = null;

  @observable
  private _loanRangesFetched: boolean = false;

  @action
  setLoanRanges(loanRanges: LoanRangesDto) {
    this._loanRanges = loanRanges;
    this._loanRangesFetched = true;
  }

  @action
  resetLoanRanges() {
    this._loanRanges = undefined;
    this._loanRangesFetched = false;
    this._loanRangesError = null;
  }

  @action
  setLoanRangesError(status: number | null, message: string | null) {
    this._loanRangesError = status !== null ? { status, message: message || '' } : null;
  }

  @action
  setLoanRangesFetched(fetched: boolean) {
    this._loanRangesFetched = fetched;
  }

  @computed
  get loanRanges() {
    return this._loanRanges;
  }

  @computed
  get loanRangesError() {
    return this._loanRangesError;
  }

  @computed
  get loanRangesErrorStatus() {
    return this._loanRangesError?.status || 0;
  }

  @computed
  get loanRangesErrorMessage() {
    return this._loanRangesError?.message || '';
  }

  @computed
  get loanRangesFetched() {
    return this._loanRangesFetched;
  }

  @computed
  get hasLoanRanges() {
    return !!this._loanRanges;
  }

  @computed
  get maxPaymentTerm() {
    return this._loanRanges ? this._loanRanges.paymentTerms.sort((a, b) => b - a)[0] : 0;
  }
}
